import type {
  CreatePreferenceCardDto,
  PaginatedPreferenceCardsResponseDto,
  PreferenceCardsFilterParams,
  UpdatePreferenceCardDto,
} from '@peritasai/types';
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type { PreferenceCardListItemEntity } from './preference-card-list.entity';
import { preferenceCardsRepository } from './repository';

export const preferenceCardsKeys = {
  all: ['preference-cards'] as const,
  lists: () => [...preferenceCardsKeys.all, 'list'] as const,
  list: (filters: PreferenceCardsFilterParams) =>
    [...preferenceCardsKeys.lists(), { filters }] as const,
} as const;

export function usePreferenceCards(
  filters: PreferenceCardsFilterParams = {}
): UseQueryResult<PaginatedPreferenceCardsResponseDto & { preferenceCards: PreferenceCardListItemEntity[] }> {
  return useQuery({
    queryKey: preferenceCardsKeys.list(filters),
    queryFn: () => preferenceCardsRepository.getPreferenceCardsPaginated(filters),
    placeholderData: keepPreviousData,
    staleTime: 30 * 1000,
  });
}

export function useCreatePreferenceCard(): UseMutationResult<
  PreferenceCardListItemEntity | null,
  Error,
  CreatePreferenceCardDto
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (dto: CreatePreferenceCardDto) =>
      preferenceCardsRepository.createPreferenceCard(dto),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: preferenceCardsKeys.lists() });
    },
  });
}

export function useCreatePreferenceCardVersion(): UseMutationResult<
  PreferenceCardListItemEntity | null,
  Error,
  { id: string; dto: UpdatePreferenceCardDto }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: UpdatePreferenceCardDto }) =>
      preferenceCardsRepository.createPreferenceCardVersion(id, dto),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: preferenceCardsKeys.lists() });
    },
  });
}
